import {
  LayoutDashboard,
  Receipt,
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  Code2,
  Settings,
  ChevronRight,
  Server,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/ui/components/primitives/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/ui/components/primitives/collapsible";
import { ExternalLink } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useRoleDetection } from "@/context/AuthContext";

export function AppSidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { isAdmin } = useRoleDetection();

  // Dynamic menu items based on user role
  const getMenuItems = () => {
    const baseItems = [
      {
        title: "Overview",
        items: [
          { title: "Dashboard", icon: LayoutDashboard, page: "dashboard" },
          { title: "Transactions", icon: Receipt, page: "transactions" },
        ],
      },
    ];

    // Add Operations section only for merchants (not admins)
    if (!isAdmin) {
      baseItems.push({
        title: "Operations",
        items: [
          { title: "Create Payin", icon: ArrowDownToLine, page: "create-payin" },
          { title: "Create Payout", icon: ArrowUpFromLine, page: "create-payout" },
        ],
      });
    }

    // Role-specific management items
    const managementItems = [];

    if (isAdmin) {
      managementItems.push(
        { title: "Approvals", icon: ChevronRight, page: "approvals" },
        { title: "PSP Management", icon: Server, page: "psp-management" },
        { title: "Settings", icon: Settings, page: "settings" }
      );
    } else {
      // Merchant user items
      managementItems.push(
        { title: "Wallet & Settlements", icon: Wallet, page: "wallet" },
        { title: "API & Webhooks", icon: Code2, page: "api-keys" },
        { title: "Settings", icon: Settings, page: "settings" }
      );
    }

    if (managementItems.length > 0) {
      baseItems.push({
        title: "Management",
        items: managementItems,
      });
    }

    return baseItems;
  };

  const menuItems = getMenuItems();

  const pathMap: Record<string, string> = {
    dashboard: "/dashboard",
    transactions: "/transactions",
    "create-payin": "/create-payin",
    "create-payout": "/create-payout",
    wallet: "/wallet",
    "api-keys": "/api-keys",
    settings: "/settings",
    approvals: "/approvals",
    "psp-management": "/admin/psp-management",
  };

  return (
    <Sidebar>
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-semibold">PG</span>
          </div>
          <div>
            <h2 className="text-sm font-semibold">PayGate</h2>
            <p className="text-xs text-muted-foreground">Payment Platform</p>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent className="p-2">
        {menuItems.map((group) => (
          <SidebarGroup key={group.title}>
            <SidebarGroupLabel className="px-2 text-xs text-muted-foreground">
              {group.title}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      onClick={() => navigate(pathMap[item.page])}
                      isActive={pathname === pathMap[item.page]}
                      tooltip={item.title}
                      className="px-2 py-2"
                    >
                      <item.icon className="h-4 w-4" strokeWidth={1.5} />
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter className="p-4 border-t border-sidebar-border">
        <a
          href={(import.meta as any).env?.VITE_DOCS_URL || "https://docs.paygate.sbs"}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 group"
        >
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <ExternalLink className="h-4 w-4 text-primary" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">Documentation</p>
            <p className="text-xs text-muted-foreground truncate">docs.paygate.sbs</p>
          </div>
        </a>
      </SidebarFooter>
    </Sidebar>
  );
}
